import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Home, MapPin, Bed, Bath, Maximize, Heart, Eye, GitCompare } from 'lucide-react';
import { toast } from 'sonner';

/** Compute the max discount rate applied on a property (base + best duration + seasonal). */
export function getTotalDiscount(property) {
  const baseDiscount = property.discount_rate || 0;
  const maxDurationDiscount = Math.max(
    property.duration_discounts?.['3months'] || 0,
    property.duration_discounts?.['6months'] || 0,
    property.duration_discounts?.['12months'] || 0,
  );
  return baseDiscount + maxDurationDiscount + (property.seasonal_discount || 0);
}

export function calculateDiscountedPrice(property) {
  return property.price * (1 - getTotalDiscount(property) / 100);
}

export function PropertyCard({ property, isSaved, onToggleSave, compare }) {
  const navigate = useNavigate();
  const inCompare = compare.has(property.id);
  const discount = getTotalDiscount(property);

  const handleCompareClick = (e) => {
    e.stopPropagation();
    if (inCompare) {
      compare.remove(property.id);
      toast.success('Removed from Compare');
      return;
    }
    const res = compare.add(property.id);
    if (res.full) {
      toast.error(`You can compare up to ${compare.max} properties. Remove one first.`);
      return;
    }
    toast.success('Added to Compare');
  };

  return (
    <Card className="property-card overflow-hidden border border-border shadow-sm">
      <div
        className="relative h-48 overflow-hidden bg-muted cursor-pointer"
        onClick={() => navigate(`/property/${property.id}`)}
      >
        {property.images && property.images.length > 0 ? (
          <img src={property.images[0]} alt={property.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Home className="w-12 h-12 text-muted-foreground" />
          </div>
        )}
        {discount > 0 && (
          <div className="absolute top-3 left-3 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-bold tracking-wide">
            {discount.toFixed(0)}% OFF
          </div>
        )}
        <button
          data-testid={`save-property-${property.id}-btn`}
          onClick={(e) => { e.stopPropagation(); onToggleSave(property.id); }}
          className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
        >
          <Heart className={`w-5 h-5 ${isSaved ? 'fill-red-500 text-red-500' : 'text-gray-700'}`} />
        </button>
        <button
          data-testid={`compare-property-${property.id}-btn`}
          onClick={handleCompareClick}
          title={inCompare ? 'Remove from Compare' : 'Add to Compare'}
          aria-label={inCompare ? 'Remove from Compare' : 'Add to Compare'}
          className={`absolute top-14 right-3 w-10 h-10 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors ${
            inCompare
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-white/90 text-gray-700 hover:bg-white'
          }`}
        >
          <GitCompare className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-medium mb-2">{property.title}</h3>
        <div className="flex items-center text-sm text-muted-foreground mb-4">
          <MapPin className="w-4 h-4 mr-1" />
          {property.city}
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
          <div className="flex items-center"><Bed className="w-4 h-4 mr-1" />{property.bedrooms}</div>
          <div className="flex items-center"><Bath className="w-4 h-4 mr-1" />{property.bathrooms}</div>
          <div className="flex items-center"><Maximize className="w-4 h-4 mr-1" />{property.area} sqft</div>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
          <div className="flex items-center gap-1"><Eye className="w-3 h-3" />{property.views_count || 0} views</div>
          <div className="flex items-center gap-1"><Heart className="w-3 h-3" />{property.likes_count || 0} likes</div>
        </div>
        <div className="mb-4">
          <div className="text-2xl font-bold text-primary">
            ${calculateDiscountedPrice(property).toFixed(0)}
            <span className="text-sm font-normal text-muted-foreground">/month</span>
          </div>
          {discount > 0 && (
            <div className="text-sm text-muted-foreground line-through">${property.price}</div>
          )}
        </div>
        <Button
          data-testid={`view-property-${property.id}-btn`}
          onClick={() => navigate(`/property/${property.id}`)}
          className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover"
        >
          View Details
        </Button>
      </div>
    </Card>
  );
}
